import { c as _c } from "react/compiler-runtime";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { WorldMap } from "@components/world-map/world-map";
import { CollapseIcon, ExpandIcon, XMarkCircleFillIcon } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { ProjectMarkerPopupContent } from "@pages/projects/projects-map/project-marker-popup-content";
import { ProjectsMapContainer } from "@pages/projects/projects-map/projects-map-container";
import { ProjectsMapEmptyOverlay } from "@pages/projects/projects-map/projects-map-empty-overlay";
import { ProjectsMapInfoPage } from "@pages/projects/projects-map/projects-map-info-page";
import { ProjectsMapList } from "@pages/projects/projects-map/projects-map-list";
import { ProjectsMapSkeleton } from "@pages/projects/projects-map/projects-map-skeleton";
import { useProjectsMapDeepLink } from "@pages/projects/projects-map/use-projects-map-deep-link";
import { useProjectsMapLoadTracking } from "@pages/projects/projects-map/use-projects-map-load-tracking";
import { WORLD_MAP_CONTROL_ICON_SIZE } from "@components/world-map/world-map-constants";
import { mapProjectsByArchivingStateSelector, mapProjectsFetchingStatusSelector } from "@store/projects-map/projects-map-selectors";
import { fetchMapProjects } from "@store/projects-map/projects-map-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector, selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { searchSelector } from "@store/ui/ui-selector";
import { getSvgColoredIconCss } from "@styles/common-styles";
import { MAP_EXPORT_EXCLUDED_COLUMNS } from "@utils/file-data-export";
import { sentryCaptureError } from "@utils/sentry-utils";
import { sortProjects } from "@utils/project-utils";
import { matchesAnySearchWord } from "@utils/string-utils";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";
import { PALETTE_VARS } from "@styles/palette-vars";
const EMPTY_SET = new Set();
/** World map that displays projects */
export function ProjectsMap(t0) {
  const $ = _c(165);
  const {
    companyId,
    projectArchivingState,
    sortBy,
    setSortBy
  } = t0;
  const dispatch = useAppDispatch();
  const {
    isScreenLgOrLarger
  } = useMediaQueryList();
  const mapRef = useRef(null);
  const {
    initialView,
    onViewStable,
    resetView
  } = useProjectsMapDeepLink();
  const {
    handleMapReady,
    handleProjectsFetchStart,
    handleProjectsFetchEnd
  } = useProjectsMapLoadTracking();
  const {
    trackEvent
  } = useTrackEvent();
  const [viewportProjectIds, setViewportProjectIds] = useState(null);
  const [highlightedIds, setHighlightedIds] = useState(EMPTY_SET);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  let t1;
  if ($[0] !== isMapFullscreen || $[1] !== trackEvent) {
    t1 = () => {
      trackEvent({
        name: WorldMapEvents.useMapControl,
        props: {
          control: "fullscreen",
          controlValue: isMapFullscreen ? "exitFullscreen" : "enterFullscreen",
          mapContext: "projects map"
        }
      });
      setIsMapFullscreen(_temp);
    };
    $[0] = isMapFullscreen;
    $[1] = trackEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleMapFullscreen = t1;
  const fetchingStatus = useAppSelector(mapProjectsFetchingStatusSelector);
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selectedEntitiesSelector("mapProjects");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedMapProjects = useAppSelector(t2);
  const isAnyRowSelected = selectedMapProjects.length !== 0;
  let t3;
  if ($[4] !== projectArchivingState) {
    t3 = mapProjectsByArchivingStateSelector(projectArchivingState);
    $[4] = projectArchivingState;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mapProjectsSelector = t3;
  const allProjects = useAppSelector(mapProjectsSelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t4;
  if ($[6] !== debouncedSearchText) {
    t4 = debouncedSearchText.trim() || undefined;
    $[6] = debouncedSearchText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const search = t4;
  const hasSearchQuery = search !== undefined;
  const isLoading = fetchingStatus === "uninitialized" || fetchingStatus === "pending";
  let t5;
  bb0: {
    if (!search) {
      t5 = allProjects;
      break bb0;
    }
    let t6;
    if ($[8] !== allProjects || $[9] !== search) {
      let t7;
      if ($[11] !== search) {
        t7 = p => matchesAnySearchWord(p.name, search ?? "");
        $[11] = search;
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      t6 = allProjects.filter(t7);
      $[8] = allProjects;
      $[9] = search;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = t6;
  }
  const filteredProjects = t5;
  let t6;
  if ($[13] !== filteredProjects) {
    t6 = filteredProjects.filter(isSdbProjectWithPosition);
    $[13] = filteredProjects;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const filteredProjectsWithPosition = t6;
  let t7;
  if ($[15] !== filteredProjectsWithPosition || $[16] !== viewportProjectIds) {
    t7 = viewportProjectIds ? filteredProjectsWithPosition.filter(p_0 => viewportProjectIds.has(p_0.id)) : filteredProjectsWithPosition;
    $[15] = filteredProjectsWithPosition;
    $[16] = viewportProjectIds;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const inViewport = t7;
  let t8;
  if ($[18] !== inViewport || $[19] !== sortBy) {
    t8 = sortProjects(inViewport, sortBy);
    $[18] = inViewport;
    $[19] = sortBy;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const visibleProjects = t8;
  let t9;
  if ($[21] !== visibleProjects) {
    t9 = new Set(visibleProjects.map(_temp2));
    $[21] = visibleProjects;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const visibleIds = t9;
  let t10;
  if ($[23] !== filteredProjects || $[24] !== sortBy || $[25] !== visibleIds) {
    let t11;
    if ($[27] !== visibleIds) {
      t11 = p_2 => !visibleIds.has(p_2.id);
      $[27] = visibleIds;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    const remaining = filteredProjects.filter(t11);
    t10 = sortProjects(remaining, sortBy);
    $[23] = filteredProjects;
    $[24] = sortBy;
    $[25] = visibleIds;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const otherProjects = t10;
  let t11;
  if ($[29] !== otherProjects || $[30] !== visibleProjects) {
    t11 = [...visibleProjects, ...otherProjects];
    $[29] = otherProjects;
    $[30] = visibleProjects;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const projectsMapList = t11;
  const hasNoProjectsWithPosition = fetchingStatus === "succeeded" && filteredProjects.length > 0 && filteredProjectsWithPosition.length === 0;
  const [wasMapEmptyOnLoad, setWasMapEmptyOnLoad] = useState(null);
  let t12;
  let t13;
  if ($[32] !== allProjects || $[33] !== fetchingStatus || $[34] !== wasMapEmptyOnLoad) {
    t12 = () => {
      if (fetchingStatus !== "succeeded" || wasMapEmptyOnLoad !== null) {
        return;
      }
      setWasMapEmptyOnLoad(!allProjects.some(isSdbProjectWithPosition));
    };
    t13 = [fetchingStatus, allProjects, wasMapEmptyOnLoad];
    $[32] = allProjects;
    $[33] = fetchingStatus;
    $[34] = wasMapEmptyOnLoad;
    $[35] = t12;
    $[36] = t13;
  } else {
    t12 = $[35];
    t13 = $[36];
  }
  useEffect(t12, t13);
  const shouldShowEmptyCaption = wasMapEmptyOnLoad === true && hasNoProjectsWithPosition && !hasSearchQuery;
  const [isMapNavigated, setIsMapNavigated] = useState(false);
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setIsMapNavigated(true);
    };
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const markMapNavigated = t14;
  let t15;
  if ($[38] !== onViewStable) {
    t15 = view => {
      markMapNavigated();
      onViewStable(view);
    };
    $[38] = onViewStable;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const handleViewStable = t15;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = entityId => {
      setHighlightedIds(entityId ? new Set([entityId]) : EMPTY_SET);
      mapRef.current?.highlightEntity(entityId);
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const onMarkerHover = t16;
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = memberIds => {
      setHighlightedIds(memberIds.length > 0 ? new Set(memberIds) : EMPTY_SET);
    };
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const onClusterHover = t17;
  let t18;
  if ($[42] !== projectsMapList) {
    t18 = new Set(projectsMapList.map(_temp3));
    $[42] = projectsMapList;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const projectsMapListIds = t18;
  let t19;
  if ($[44] !== isMapFullscreen) {
    t19 = isMapFullscreen ? <CollapseIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} /> : <ExpandIcon sx={{
      width: WORLD_MAP_CONTROL_ICON_SIZE,
      height: WORLD_MAP_CONTROL_ICON_SIZE
    }} />;
    $[44] = isMapFullscreen;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const t20 = isMapFullscreen ? "Exit full screen" : "Full screen";
  let t21;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== toggleMapFullscreen) {
    t21 = [{
      icon: t19,
      tooltip: t20,
      onClick: toggleMapFullscreen
    }];
    $[46] = t19;
    $[47] = t20;
    $[48] = toggleMapFullscreen;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = entities => setViewportProjectIds(new Set(entities.map(_temp4)));
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== filteredProjectsWithPosition || $[52] !== handleMapReady || $[53] !== handleViewStable || $[54] !== initialView || $[55] !== resetView || $[56] !== t21) {
    t23 = <WorldMap entities={filteredProjectsWithPosition} mapContext="projects map" shouldShowMapControls={true} buttons={t21} onVisibleEntitiesChange={t22} mapRef={mapRef} initialView={initialView} onViewStable={handleViewStable} onResetView={resetView} onMapReady={handleMapReady} onEntityHover={onMarkerHover} onClusterHover={onClusterHover} renderPopupContent={_temp5} />;
    $[51] = filteredProjectsWithPosition;
    $[52] = handleMapReady;
    $[53] = handleViewStable;
    $[54] = initialView;
    $[55] = resetView;
    $[56] = t21;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const worldMapElement = t23;
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = (position, zoom) => {
      mapRef.current?.highlightEntity(null);
      mapRef.current?.goTo(position, zoom);
    };
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const goTo = t24;
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = entityId_0 => {
      mapRef.current?.highlightEntity(entityId_0);
    };
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  const onHoverChange = t25;
  let t26;
  if ($[60] !== companyId || $[61] !== hasNoProjectsWithPosition || $[62] !== hasSearchQuery || $[63] !== highlightedIds || $[64] !== otherProjects || $[65] !== projectArchivingState || $[66] !== sortBy || $[67] !== visibleProjects) {
    t26 = <ProjectsMapList visibleProjects={visibleProjects} otherProjects={otherProjects} companyId={companyId} projectArchivingState={projectArchivingState} goTo={goTo} onHoverChange={onHoverChange} highlightedProjectIds={highlightedIds} hasSearchQuery={hasSearchQuery} hasNoProjectsWithPosition={hasNoProjectsWithPosition} sortBy={sortBy} />;
    $[60] = companyId;
    $[61] = hasNoProjectsWithPosition;
    $[62] = hasSearchQuery;
    $[63] = highlightedIds;
    $[64] = otherProjects;
    $[65] = projectArchivingState;
    $[66] = sortBy;
    $[67] = visibleProjects;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  const projectsMapListElement = t26;
  let t27;
  if ($[69] !== companyId || $[70] !== dispatch || $[71] !== handleProjectsFetchEnd || $[72] !== handleProjectsFetchStart) {
    t27 = async () => {
      handleProjectsFetchStart();
      ;
      try {
        const projects = await dispatch(fetchMapProjects({
          companyId
        })).unwrap();
        handleProjectsFetchEnd({
          outcome: "succeeded",
          projectCount: projects.length,
          mappableProjectCount: projects.filter(isSdbProjectWithPosition).length
        });
      } catch (t28) {
        const error = t28;
        handleProjectsFetchEnd({
          outcome: "failed",
          projectCount: 0,
          mappableProjectCount: 0
        });
        sentryCaptureError({
          error,
          title: "Failed to load the map projects."
        });
      }
    };
    $[69] = companyId;
    $[70] = dispatch;
    $[71] = handleProjectsFetchEnd;
    $[72] = handleProjectsFetchStart;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  const getProjects = t27;
  let t28;
  let t29;
  if ($[74] !== fetchingStatus || $[75] !== getProjects) {
    t28 = () => {
      if (fetchingStatus === "uninitialized") {
        getProjects();
      }
    };
    t29 = [fetchingStatus, getProjects];
    $[74] = fetchingStatus;
    $[75] = getProjects;
    $[76] = t28;
    $[77] = t29;
  } else {
    t28 = $[76];
    t29 = $[77];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[78] !== dispatch || $[79] !== projectsMapListIds || $[80] !== selectedRowIds) {
    t30 = () => {
      if (selectedRowIds.length === 0) {
        return;
      }
      const nextSelectedRowIds = selectedRowIds.filter(rowId => projectsMapListIds.has(rowId));
      if (nextSelectedRowIds.length !== selectedRowIds.length) {
        dispatch(setInitialFetchingItems(nextSelectedRowIds));
      }
    };
    t31 = [dispatch, projectsMapListIds, selectedRowIds];
    $[78] = dispatch;
    $[79] = projectsMapListIds;
    $[80] = selectedRowIds;
    $[81] = t30;
    $[82] = t31;
  } else {
    t30 = $[81];
    t31 = $[82];
  }
  useEffect(t30, t31);
  const isToolbarActionsReady = fetchingStatus === "succeeded" && viewportProjectIds !== null && projectsMapList.length > 0;
  const isListReady = fetchingStatus === "succeeded" && viewportProjectIds !== null;
  const projectsForExport = isAnyRowSelected ? selectedMapProjects : projectsMapList;
  let t32;
  if ($[83] !== companyId || $[84] !== isToolbarActionsReady || $[85] !== projectArchivingState || $[86] !== projectsForExport) {
    t32 = isToolbarActionsReady ? <BulkExportProjects companyId={companyId} selectedProjects={projectsForExport} subjectType="workspace" projectStatus={projectArchivingState} areEntitiesSelected={true} buttonType="standard" columnsToExclude={MAP_EXPORT_EXCLUDED_COLUMNS} viewMode="map" /> : undefined;
    $[83] = companyId;
    $[84] = isToolbarActionsReady;
    $[85] = projectArchivingState;
    $[86] = projectsForExport;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  const exportComponent = t32;
  const t33 = hasSearchQuery ? allProjects.length : undefined;
  let t34;
  if ($[88] !== isToolbarActionsReady || $[89] !== setSortBy || $[90] !== sortBy) {
    t34 = isToolbarActionsReady && <ProjectsSort key="projects-sort" sortBy={sortBy} setSortBy={setSortBy} />;
    $[88] = isToolbarActionsReady;
    $[89] = setSortBy;
    $[90] = sortBy;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== exportComponent || $[93] !== filteredProjects.length || $[94] !== isLoading || $[95] !== isScreenLgOrLarger || $[96] !== isToolbarActionsReady || $[97] !== t33 || $[98] !== t34) {
    t35 = <EntitiesToolbar toolbarEntityName="Project" numberOfRows={filteredProjects.length} totalNumberOfRows={t33} isLoading={isLoading} shouldShowRowsLabel={isToolbarActionsReady} shouldStack={isScreenLgOrLarger} shouldRemoveTopMargin={true} sortActions={t34} exportComponent={exportComponent} shouldSkipValidation={true} />;
    $[92] = exportComponent;
    $[93] = filteredProjects.length;
    $[94] = isLoading;
    $[95] = isScreenLgOrLarger;
    $[96] = isToolbarActionsReady;
    $[97] = t33;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  const toolbarElement = t35;
  let t36;
  if ($[100] !== fetchingStatus || $[101] !== isListReady || $[102] !== projectsMapListElement) {
    t36 = function renderListContent() {
      if (fetchingStatus === "rejected") {
        return <ProjectsMapInfoPage title="Failed to load the map projects" subtitle="Please reload the page and try again." source={_temp6} />;
      }
      if (!isListReady) {
        return <ProjectsMapSkeleton />;
      }
      return projectsMapListElement;
    };
    $[100] = fetchingStatus;
    $[101] = isListReady;
    $[102] = projectsMapListElement;
    $[103] = t36;
  } else {
    t36 = $[103];
  }
  const renderListContent = t36;
  let t37;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      width: "100%",
      height: "100%",
      position: "relative"
    };
    $[104] = t37;
  } else {
    t37 = $[104];
  }
  let t38;
  if ($[105] !== isMapNavigated || $[106] !== projectArchivingState || $[107] !== shouldShowEmptyCaption) {
    t38 = shouldShowEmptyCaption && <ProjectsMapEmptyOverlay projectArchivingState={projectArchivingState} isDismissed={isMapNavigated} />;
    $[105] = isMapNavigated;
    $[106] = projectArchivingState;
    $[107] = shouldShowEmptyCaption;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== t38 || $[110] !== worldMapElement) {
    t39 = <Box onPointerDown={markMapNavigated} onWheel={markMapNavigated} sx={t37}>{worldMapElement}{t38}</Box>;
    $[109] = t38;
    $[110] = worldMapElement;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  const mapElement = t39;
  if (isScreenLgOrLarger) {
    let t40;
    if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = {
        overflow: "hidden",
        height: "100%"
      };
      $[112] = t40;
    } else {
      t40 = $[112];
    }
    let t41;
    if ($[113] !== isMapFullscreen) {
      t41 = isMapFullscreen && {
        display: "none"
      };
      $[113] = isMapFullscreen;
      $[114] = t41;
    } else {
      t41 = $[114];
    }
    let t42;
    if ($[115] !== t41) {
      t42 = {
        width: "450px",
        flexShrink: 0,
        height: "100%",
        pr: 1,
        ...t41
      };
      $[115] = t41;
      $[116] = t42;
    } else {
      t42 = $[116];
    }
    let t43;
    if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = {
        flex: 1,
        minHeight: 0
      };
      $[117] = t43;
    } else {
      t43 = $[117];
    }
    let t44;
    if ($[118] !== renderListContent) {
      t44 = <Box sx={t43}>{renderListContent()}</Box>;
      $[118] = renderListContent;
      $[119] = t44;
    } else {
      t44 = $[119];
    }
    let t45;
    if ($[120] !== t42 || $[121] !== t44 || $[122] !== toolbarElement) {
      t45 = <Stack sx={t42}>{toolbarElement}{t44}</Stack>;
      $[120] = t42;
      $[121] = t44;
      $[122] = toolbarElement;
      $[123] = t45;
    } else {
      t45 = $[123];
    }
    let t46;
    if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = {
        flex: 1,
        minWidth: 0,
        height: "100%"
      };
      $[124] = t46;
    } else {
      t46 = $[124];
    }
    let t47;
    if ($[125] !== mapElement) {
      t47 = <Box key="projects-map" sx={t46}>{mapElement}</Box>;
      $[125] = mapElement;
      $[126] = t47;
    } else {
      t47 = $[126];
    }
    let t48;
    if ($[127] !== t45 || $[128] !== t47) {
      t48 = <ProjectsMapContainer><Stack direction="row" sx={t40}>{t45}{t47}</Stack></ProjectsMapContainer>;
      $[127] = t45;
      $[128] = t47;
      $[129] = t48;
    } else {
      t48 = $[129];
    }
    return t48;
  }
  let t40;
  if ($[130] !== isMapFullscreen) {
    t40 = isMapFullscreen && {
      display: "none"
    };
    $[130] = isMapFullscreen;
    $[131] = t40;
  } else {
    t40 = $[131];
  }
  let t41;
  if ($[132] !== t40) {
    t41 = {
      ...t40
    };
    $[132] = t40;
    $[133] = t41;
  } else {
    t41 = $[133];
  }
  let t42;
  if ($[134] !== t41 || $[135] !== toolbarElement) {
    t42 = <Box sx={t41}>{toolbarElement}</Box>;
    $[134] = t41;
    $[135] = toolbarElement;
    $[136] = t42;
  } else {
    t42 = $[136];
  }
  let t43;
  if ($[137] !== isMapFullscreen) {
    t43 = [isMapFullscreen];
    $[137] = isMapFullscreen;
    $[138] = t43;
  } else {
    t43 = $[138];
  }
  const t44 = isMapFullscreen ? 0 : 1;
  let t45;
  if ($[139] !== t44) {
    t45 = {
      height: "100%",
      gap: t44
    };
    $[139] = t44;
    $[140] = t45;
  } else {
    t45 = $[140];
  }
  const t46 = isMapFullscreen ? "100%" : "50%";
  let t47;
  if ($[141] !== t46) {
    t47 = {
      height: t46
    };
    $[141] = t46;
    $[142] = t47;
  } else {
    t47 = $[142];
  }
  let t48;
  if ($[143] !== mapElement || $[144] !== t47) {
    t48 = <Box key="projects-map" sx={t47}>{mapElement}</Box>;
    $[143] = mapElement;
    $[144] = t47;
    $[145] = t48;
  } else {
    t48 = $[145];
  }
  let t49;
  if ($[146] !== isMapFullscreen) {
    t49 = isMapFullscreen && {
      display: "none"
    };
    $[146] = isMapFullscreen;
    $[147] = t49;
  } else {
    t49 = $[147];
  }
  let t50;
  if ($[148] !== t49) {
    t50 = {
      height: "50%",
      ...t49
    };
    $[148] = t49;
    $[149] = t50;
  } else {
    t50 = $[149];
  }
  let t51;
  if ($[150] !== renderListContent) {
    t51 = renderListContent();
    $[150] = renderListContent;
    $[151] = t51;
  } else {
    t51 = $[151];
  }
  let t52;
  if ($[152] !== t50 || $[153] !== t51) {
    t52 = <Box sx={t50}>{t51}</Box>;
    $[152] = t50;
    $[153] = t51;
    $[154] = t52;
  } else {
    t52 = $[154];
  }
  let t53;
  if ($[155] !== t45 || $[156] !== t48 || $[157] !== t52) {
    t53 = <Stack sx={t45}>{t48}{t52}</Stack>;
    $[155] = t45;
    $[156] = t48;
    $[157] = t52;
    $[158] = t53;
  } else {
    t53 = $[158];
  }
  let t54;
  if ($[159] !== t43 || $[160] !== t53) {
    t54 = <ProjectsMapContainer heightDeps={t43}>{t53}</ProjectsMapContainer>;
    $[159] = t43;
    $[160] = t53;
    $[161] = t54;
  } else {
    t54 = $[161];
  }
  let t55;
  if ($[162] !== t42 || $[163] !== t54) {
    t55 = <>{t42}{t54}</>;
    $[162] = t42;
    $[163] = t54;
    $[164] = t55;
  } else {
    t55 = $[164];
  }
  return t55;
}
function _temp6(props) {
  return <XMarkCircleFillIcon {...props} sx={{
    ...props.sx,
    ...getSvgColoredIconCss(PALETTE_VARS.error.main),
    width: "35px",
    height: "35px"
  }} />;
}
function _temp5(project) {
  return <ProjectMarkerPopupContent project={project} />;
}
function _temp4(e) {
  return e.id;
}
function _temp3(p_3) {
  return p_3.id;
}
function _temp2(p_1) {
  return p_1.id;
}
function _temp(wasFullscreen) {
  return !wasFullscreen;
}